import { DropdownMenu } from '@kobalte/core/dropdown-menu'
import { createEffect, For, Index, onCleanup, onMount, Show } from 'solid-js'
import type { Layer } from '../audio/engine'
import { useApp } from '../controller'
import { PRESETS } from '../model/presets'
import { FAMILIES, FAMILY_INFO, type Family, MAX_OBJECTS, MAX_SOURCES } from '../model/schema'
import { Viewport } from '../viewport/viewport'
import { Btn, Meter, NumberInput, Segmented, SwitchRow } from './controls'
import { round, track } from './util'

const FAMILY_DOT: Record<string, string> = {
  vent: '#6fb7d8',
  motor: '#e0a64a',
  drone: '#c4605a',
  drip: '#62d2c0',
  turbine: '#e8c36a',
  fan: '#8fd1a8',
  press: '#d98b5f',
  grind: '#d6d06a',
  creak: '#a6845c',
  clip: '#b08be0',
}

// ---- Top bar -----------------------------------------------------------------------------------------

export function TopBar() {
  const app = useApp()
  const { store, ui } = app
  let fileInput!: HTMLInputElement
  return (
    <header class="topbar">
      <div class="brand">
        <span class="brand__mark" aria-hidden="true" />
        <span class="brand__name">MEGASPACE</span>
        <span class="brand__scene" data-testid="scene-name">
          {store.doc.name}
        </span>
      </div>

      <div class="topbar__group">
        <Segmented
          label="Mode"
          testid="mode"
          value={ui.mode}
          onChange={(m) => {
            if (m !== ui.mode) void app.toggleExplore()
          }}
          options={[
            { value: 'edit', label: 'Edit', title: 'Orbit camera, drag the head' },
            { value: 'explore', label: 'Explore', title: 'Walk through the space as the listener (WASD, mouse look, Esc to exit)' },
          ]}
        />
      </div>

      <div class="topbar__group">
        <Btn small disabled={!store.canUndo()} onClick={store.undo} title={`Undo ${store.undoLabel() ?? ''} (Ctrl+Z)`} testid="undo">
          ↶ Undo
        </Btn>
        <Btn small disabled={!store.canRedo()} onClick={store.redo} title={`Redo ${store.redoLabel() ?? ''} (Ctrl+Shift+Z)`} testid="redo">
          ↷ Redo
        </Btn>
      </div>

      <div class="topbar__group">
        <DropdownMenu>
          <DropdownMenu.Trigger class="btn btn--small" data-testid="new-menu">
            New ▾
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content class="menu">
              <For each={PRESETS}>
                {(p) => (
                  <DropdownMenu.Item class="menu__item" onSelect={() => app.newFromPreset(p.id)} data-testid={`preset-${p.id}`}>
                    <strong>{p.label}</strong>
                    <small>{p.description}</small>
                  </DropdownMenu.Item>
                )}
              </For>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu>
        <Btn small onClick={() => fileInput.click()} title="Open a .zip project" testid="open">
          Open
        </Btn>
        <Btn small onClick={app.saveZip} title="Save the project as a .zip" testid="save">
          Save
        </Btn>
        <input
          ref={fileInput}
          type="file"
          accept=".zip,application/zip"
          hidden
          data-testid="open-input"
          onChange={(e) => {
            const f = e.currentTarget.files?.[0]
            if (f) void app.openZip(f)
            e.currentTarget.value = ''
          }}
        />
      </div>

      <div class="topbar__spacer" />

      <div class="topbar__group topbar__audio">
        <Show
          when={ui.audioStatus === 'on'}
          fallback={
            <Btn
              accent
              onClick={() => void app.startAudio()}
              disabled={ui.audioStatus === 'starting'}
              testid="start-audio"
              title="Audio only starts after you press this"
            >
              {ui.audioStatus === 'starting' ? 'Starting…' : '▶ Start Audio'}
            </Btn>
          }
        >
          <Btn small onClick={() => void app.stopAudio()} testid="stop-audio" title="Stop audio and release the device">
            ■ Audio on
          </Btn>
        </Show>
        <div class="master">
          <span>Vol</span>
          <input
            type="range"
            min="0"
            max="1.5"
            step="0.01"
            aria-label="Master volume"
            value={store.doc.settings.masterGain}
            onInput={(e) => store.patch({ c: 'settings' }, { masterGain: e.currentTarget.valueAsNumber }, 'Master volume', true)}
          />
        </div>
        <Meter level={ui.level} />
      </div>
    </header>
  )
}

// ---- Outliner ----------------------------------------------------------------------------------------

export function Outliner() {
  const app = useApp()
  const { store, ui } = app
  let clipInput!: HTMLInputElement
  const is = (kind: string, id?: string) => ui.selection?.kind === kind && (id === undefined || (ui.selection as { id?: string }).id === id)

  return (
    <aside class="panel panel--left" aria-label="Scene outline" data-testid="outliner">
      <div class="outline">
        <button
          type="button"
          class="outline__row"
          classList={{ 'is-selected': is('room') }}
          onClick={() => app.select({ kind: 'room' })}
          data-testid="select-room"
        >
          <span class="dot dot--room" /> Space{' '}
          <small class="outline__tag">{store.doc.enclosure.size.map((v) => round(v, 0)).join(' × ')} m</small>
        </button>
        <button
          type="button"
          class="outline__row"
          classList={{ 'is-selected': is('head') }}
          onClick={() => app.select({ kind: 'head' })}
          data-testid="select-head"
        >
          <span class="dot dot--head" /> Listener head
        </button>
      </div>

      <div class="outline__head">
        <h3>
          Sources{' '}
          <small>
            {store.doc.sources.length}/{MAX_SOURCES}
          </small>
        </h3>
        <DropdownMenu>
          <DropdownMenu.Trigger class="btn btn--small" data-testid="add-source" disabled={store.doc.sources.length >= MAX_SOURCES}>
            + Add ▾
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content class="menu">
              <For each={FAMILIES}>
                {(f) => (
                  <DropdownMenu.Item class="menu__item" onSelect={() => app.addSource(f as Family)} data-testid={`add-${f}`}>
                    <strong>{FAMILY_INFO[f].label}</strong>
                    <small>{FAMILY_INFO[f].controls.join(' · ')}</small>
                  </DropdownMenu.Item>
                )}
              </For>
              <DropdownMenu.Separator class="menu__sep" />
              <DropdownMenu.Item class="menu__item" onSelect={() => clipInput.click()} data-testid="add-clip">
                <strong>Import clip…</strong>
                <small>WAV, MP3, OGG — or drop a file on the viewport</small>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu>
        <input
          ref={clipInput}
          type="file"
          accept="audio/*"
          hidden
          data-testid="clip-input"
          onChange={(e) => {
            const f = e.currentTarget.files?.[0]
            if (f) void app.importClip(f)
            e.currentTarget.value = ''
          }}
        />
      </div>
      <div class="outline" data-testid="source-list">
        <Index each={store.doc.sources} fallback={<p class="note">No sources. Add one to start listening.</p>}>
          {(s) => (
            <div class="outline__row outline__row--split" classList={{ 'is-selected': is('source', s().id) }}>
              <button
                type="button"
                class="outline__main"
                onClick={() => app.select({ kind: 'source', id: s().id })}
                data-testid={`source-${s().name}`}
              >
                <span
                  class="dot"
                  style={{ background: FAMILY_DOT[s().kind === 'clip' ? 'clip' : s().family] }}
                  classList={{ 'is-dim': !s().playing || s().mute }}
                />
                <span class="outline__name">{s().name}</span>
              </button>
              <button
                type="button"
                class="mini"
                classList={{ 'is-on': s().playing }}
                title={s().playing ? 'Stop' : 'Play'}
                aria-label={`${s().playing ? 'Stop' : 'Play'} ${s().name}`}
                onClick={() => store.patch({ c: 'sources', id: s().id }, { playing: !s().playing }, 'Play/stop')}
              >
                {s().playing ? '■' : '▶'}
              </button>
              <button
                type="button"
                class="mini"
                classList={{ 'is-on': s().mute }}
                title="Mute"
                aria-label={`Mute ${s().name}`}
                onClick={() => store.patch({ c: 'sources', id: s().id }, { mute: !s().mute }, 'Mute')}
              >
                M
              </button>
              <button
                type="button"
                class="mini"
                classList={{ 'is-on': s().solo }}
                title="Solo"
                aria-label={`Solo ${s().name}`}
                onClick={() => store.patch({ c: 'sources', id: s().id }, { solo: !s().solo }, 'Solo')}
              >
                S
              </button>
            </div>
          )}
        </Index>
      </div>

      <div class="outline__head">
        <h3>
          Primitives{' '}
          <small>
            {store.doc.objects.length}/{MAX_OBJECTS}
          </small>
        </h3>
        <div class="actions">
          <Btn small onClick={() => app.addPrimitive('box')} testid="add-box">
            + Box
          </Btn>
          <Btn small onClick={() => app.addPrimitive('cylinder')} testid="add-cylinder">
            + Cyl
          </Btn>
          <Btn small onClick={() => app.addPrimitive('tube')} testid="add-tube" title="Hollow cylinder: a pipe, duct or silo">
            + Tube
          </Btn>{' '}
          <Btn
            small
            onClick={() => app.addPrimitive('prism')}
            testid="add-prism"
            title="Custom extruded shape: draw its outline in the inspector"
          >
            + Shape
          </Btn>
        </div>
      </div>
      <div class="outline outline--scroll" data-testid="object-list">
        <Index each={store.doc.objects} fallback={<p class="note">The enclosure is empty.</p>}>
          {(o) => (
            <button
              type="button"
              class="outline__row"
              classList={{ 'is-selected': is('object', o().id) }}
              onClick={() => app.select({ kind: 'object', id: o().id })}
              data-testid={`object-${o().name}`}
            >
              <span
                class="dot dot--shape"
                classList={{ 'is-round': o().kind === 'cylinder', 'is-tube': o().kind === 'tube', 'is-prism': o().kind === 'prism' }}
              />{' '}
              <span class="outline__name">{o().name}</span>
              <small class="outline__tag">{o().material}</small>
            </button>
          )}
        </Index>
      </div>
    </aside>
  )
}

// ---- Viewport ----------------------------------------------------------------------------------------

export function ViewportPanel() {
  const app = useApp()
  const { store, ui, setUi } = app
  let host!: HTMLDivElement
  let viewport: Viewport | null = null

  onMount(() => {
    viewport = new Viewport(host, app.viewportEvents)
    app.setViewport(viewport)
    ;(window as unknown as { __viewport?: Viewport }).__viewport = viewport
    viewport.frameScene()
  })
  onCleanup(() => {
    app.setViewport(null)
    viewport?.dispose()
    viewport = null
  })

  createEffect(() => {
    track(store.doc)
    viewport?.syncScene(store.plain())
  })
  createEffect(() => {
    const s = ui.selection
    viewport?.setState({
      selection: s && s.kind !== 'room' ? s : null,
      transformMode: ui.transformMode,
      view: ui.view,
      cutaway: ui.cutaway,
      showGrid: ui.showGrid,
      snapEnabled: store.doc.settings.snapEnabled,
      snapStep: store.doc.settings.snapStep,
    })
  })
  createEffect(() => {
    viewport?.setAcousticsDebug(ui.debugOverlay ? app.lastAcoustics() : null)
  })

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    const f = [...(e.dataTransfer?.files ?? [])].find(
      (x) => x.type.startsWith('audio/') || /\.(wav|mp3|ogg|flac|m4a|aac|opus)$/i.test(x.name),
    )
    if (!f) return app.toast('Drop an audio file (WAV, MP3, OGG…).', 'error')
    const at = viewport?.pointOnPlane(e.clientX, e.clientY, 1.5)
    void app.importClip(f, at ?? undefined)
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: file drop target only; keyboard users import via Add → Import clip
    <div class="viewport" onDragOver={(e) => e.preventDefault()} onDrop={onDrop} data-testid="viewport">
      <div class="viewport__canvas" ref={host} data-testid="viewport-canvas" />
      <Show when={ui.mode === 'edit'}>
        <div class="vp-toolbar vp-toolbar--tl">
          <Segmented
            label="Transform tool"
            testid="tool"
            value={ui.transformMode}
            onChange={(m) => setUi('transformMode', m)}
            options={[
              { value: 'translate', label: 'Move', title: 'Move (W)' },
              { value: 'rotate', label: 'Rotate', title: 'Rotate (E)' },
              { value: 'scale', label: 'Scale', title: 'Scale / resize (R)' },
            ]}
          />
          <SwitchRow
            label="Snap"
            checked={store.doc.settings.snapEnabled}
            onChange={(v) => store.patch({ c: 'settings' }, { snapEnabled: v }, 'Toggle snap')}
            testid="snap-toggle"
          />
          <div class="snapstep" title="Grid step in metres">
            <NumberInput
              label="Snap step"
              value={store.doc.settings.snapStep}
              min={0.05}
              max={10}
              step={0.25}
              onCommit={(v) => store.patch({ c: 'settings' }, { snapStep: v }, 'Snap step')}
            />
            <span>m</span>
          </div>
        </div>
        <div class="vp-toolbar vp-toolbar--tr">
          <Segmented
            label="View"
            testid="view"
            value={ui.view}
            onChange={(v) => setUi('view', v)}
            options={[
              { value: 'perspective', label: 'Persp' },
              { value: 'top', label: 'Top' },
              { value: 'front', label: 'Front' },
              { value: 'side', label: 'Side' },
            ]}
          />
          <SwitchRow
            label="Cutaway"
            checked={ui.cutaway}
            onChange={(v) => setUi('cutaway', v)}
            hint="Hide the walls that face the camera. They still count acoustically."
            testid="cutaway-toggle"
          />
          <SwitchRow label="Grid" checked={ui.showGrid} onChange={(v) => setUi('showGrid', v)} />
        </div>
        <div class="vp-toolbar vp-toolbar--bl">
          <Btn small onClick={app.listenHere} title="Move the head to the camera position" testid="listen-here">
            Listen here
          </Btn>
          <Btn small onClick={() => viewport?.frameScene()} title="Frame the whole space (F)">
            Frame
          </Btn>
        </div>
      </Show>
      <Show when={ui.mode === 'explore'}>
        <div class="crosshair" aria-hidden="true" />
        <div class="vp-hint" data-testid="explore-hint">
          WASD move · Shift run · wheel speed ({Math.round(ui.walkSpeed * 10) / 10} m/s) · Esc to leave
        </div>
      </Show>
    </div>
  )
}

// ---- Acoustics dock ----------------------------------------------------------------------------------

const LAYER_OPTIONS: { value: 'all' | Layer; label: string; title: string }[] = [
  { value: 'all', label: 'All', title: 'Hear everything' },
  { value: 'direct', label: 'Direct', title: 'Direct sound only' },
  { value: 'early', label: 'Early', title: 'Reflections (one and two bounces) and diffracted paths only' },
  { value: 'late', label: 'Late', title: 'Reverberant tail only' },
]

export function Dock() {
  const app = useApp()
  const { ui, setUi, store } = app
  const nameOf = (id: string) => store.doc.sources.find((s) => s.id === id)?.name ?? id
  return (
    <footer class="dock" classList={{ 'dock--open': ui.debugOverlay }} data-testid="dock">
      <div class="dock__bar">
        <span class="dock__title">Acoustics</span>
        <Segmented
          label="Acoustic layer solo"
          testid="layer"
          value={ui.layerSolo ?? 'all'}
          onChange={(v) => setUi('layerSolo', v === 'all' ? null : v)}
          options={LAYER_OPTIONS}
        />
        <Btn small onClick={app.clickTest} title="Fire a short click from the selected source (C)" testid="click-test">
          Click test
        </Btn>
        <SwitchRow
          label="Debug overlay"
          checked={ui.debugOverlay}
          onChange={(v) => setUi('debugOverlay', v)}
          testid="debug-toggle"
          hint="Draw direct and reflected paths in the viewport"
        />
        <span class="dock__legend">
          <i style={{ background: '#6fcf97' }} /> direct <i style={{ background: '#e05d5d' }} /> obstructed{' '}
          <i style={{ background: '#6aa9ff' }} /> reflection <i style={{ background: '#d98be8' }} /> diffraction
        </span>
      </div>
      <Show when={ui.debugOverlay}>
        <div class="dock__table" data-testid="debug-table">
          <Show when={app.lastAcoustics()} fallback={<p class="note">Waiting for the first acoustic result…</p>}>
            {(r) => (
              <table>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Distance</th>
                    <th>Delay</th>
                    <th>Obstruction</th>
                    <th>Tail send</th>
                    <th>Early paths: diffraction first, then reflections (path · delay)</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={r().sources}>
                    {(s) => (
                      <tr>
                        <td>{nameOf(s.id)}</td>
                        <td>{round(s.direct.length, 1)} m</td>
                        <td>{round(s.direct.delay * 1000, 1)} ms</td>
                        <td classList={{ 'is-bad': s.direct.obstructionDb > 0.5 }}>
                          {s.direct.obstructionDb > 0.5 ? `−${round(s.direct.obstructionDb, 0)} dB` : 'clear'}
                        </td>
                        <td>{round(20 * Math.log10(Math.max(s.tail.gain, 1e-6)), 1)} dB</td>
                        <td class="refl">
                          <For each={s.early}>
                            {(e) => (
                              <span title={`${e.label}: ${round(e.length, 1)} m, ${round(e.delay * 1000, 1)} ms`}>
                                {e.label}: {round(e.length, 0)} m · {round(e.delay * 1000, 0)} ms
                              </span>
                            )}
                          </For>
                          <Show when={s.blocked > 0}>
                            <span class="is-off">{s.blocked} blocked</span>
                          </Show>
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            )}
          </Show>
        </div>
      </Show>
    </footer>
  )
}

export function Toasts() {
  const app = useApp()
  return (
    <div class="toasts" aria-live="polite" data-testid="toasts">
      <For each={app.toasts()}>{(t) => <div class={`toast toast--${t.tone}`}>{t.text}</div>}</For>
    </div>
  )
}
